import { useMemo } from "react";
import protrusions from "../../../calculation/protrusions";
import type { DimensionsValues, RoofDimensionsData } from "../../../types/roof";

type UseRoofData = (props: {
  dimensions: DimensionsValues;
}) => RoofDimensionsData;

const useRoofData: UseRoofData = ({ dimensions }) => {
  const roofData = useMemo(() => {
    const roofLengthValues = filterRoofDimensions(
      dimensions,
      (key) => key.length === 1 && key !== "P"
    );

    const roofAngleValues = filterRoofDimensions(dimensions, (key) =>
      key.startsWith("P")
    );

    return { roofLengthValues, roofAngleValues };
  }, [dimensions]);

  const protrusionData = useMemo(() => {
    const selectedProtrusion = dimensions.protrusions?.[0];

    if (!selectedProtrusion) {
      return {
        protrusionLengthValues: undefined,
        protrusionAngleValues: undefined,
        protrusion: undefined
      };
    }

    const protrusionData = protrusions[selectedProtrusion.type];

    const protrusionLengthValues = filterRoofDimensions(
      selectedProtrusion,
      (key) => key.length === 1 && key !== "P"
    );

    const protrusionAngleValues = filterRoofDimensions(
      selectedProtrusion,
      (key) => key === "P"
    );

    return {
      protrusionLengthValues,
      protrusionAngleValues,
      protrusion: protrusionData
    };
  }, [dimensions]);

  return {
    ...roofData,
    ...protrusionData
  };
};

const filterRoofDimensions = (
  dimensions: DimensionsValues,
  validator: (key: string) => boolean
): Record<string, string> => {
  const entries = Object.entries(dimensions).filter(
    (roofParameter): roofParameter is [string, string] => {
      const [key] = roofParameter;
      return validator(key);
    }
  );

  return Object.fromEntries(entries);
};

export default useRoofData;
